import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useDispatch, useSelector } from "react-redux";
import { fetchSessionMasters } from "../../store/slices/sessionSlice";
import "bootstrap/dist/css/bootstrap.min.css";
import "remixicon/fonts/remixicon.css";

const SessionTypeList = ({ onAddSessionType, onEditSessionType, onDeleteSessionType }) => {
    const dispatch = useDispatch();
    const { sessionMasters, isLoading, error } = useSelector((state) => state.sessions);

    const [filterText, setFilterText] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        dispatch(fetchSessionMasters());
    }, [dispatch]);

    const filteredSessions = (sessionMasters || []).filter((s) =>
        Object.values(s || {}).some((v) =>
            String(v || "").toLowerCase().includes(filterText.toLowerCase())
        )
    );

    const totalRows = filteredSessions.length;
    const start = (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRows);

    const CustomPagination = ({ onChangePage, onChangeRowsPerPage, currentPage, rowsPerPage, rowCount }) => {
        const totalPages = Math.ceil(rowCount / rowsPerPage);

        return (
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: "0.875rem" }}>
                        Rows per page:
                    </span>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: "80px", fontSize: "0.875rem" }}
                        value={rowsPerPage}
                        onChange={(e) =>
                            onChangeRowsPerPage(Number(e.target.value), currentPage)
                        }
                    >
                        {[5, 10, 15, 20, 25].map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="d-flex align-items-center gap-1">
                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(currentPage - 1, rowCount)}
                        disabled={currentPage === 1}
                    >
                        <i className="ri-arrow-left-s-line"></i>
                    </button>

                    <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
                        Page {currentPage} of {totalPages}
                    </span>

                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(currentPage + 1, rowCount)}
                        disabled={currentPage === totalPages}
                    >
                        <i className="ri-arrow-right-s-line"></i>
                    </button>
                </div>
            </div>
        );
    };

    const columns = [
        {
            name: "S.No",
            cell: (row, index) => index + 1 + (currentPage - 1) * rowsPerPage,
            width: "80px",
        },
        {
            name: "Category",
            selector: (row) => row.category?.name || "N/A",
            sortable: true,
        },
        {
            name: "Name",
            selector: (row) => row.name || "",
            sortable: true,
        },
        {
            name: "Description",
            selector: (row) => row.description || "",
            sortable: true,
        },
        {
            name: "Status",
            selector: (row) => row.status || "active",
            sortable: true,
            cell: (row) => (
                <span className={`badge bg-label-${row.status === "active" ? "success" : "secondary"}`}>
                    {row.status ? row.status.toUpperCase() : "ACTIVE"}
                </span>
            ),
        },
        {
            name: "Created By",
            selector: (row) => row.creator?.name || "",
            sortable: true,
        },
        {
            name: "Updated By",
            selector: (row) => row.updater?.name || "",
            sortable: true,
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex justify-content-center gap-1">
                    {onEditSessionType && (
                        <button
                            className="btn btn-sm btn-outline-primary"
                            title="Edit Session Type"
                            onClick={() => onEditSessionType(row.id)}
                        >
                            <i className="ri-edit-line"></i>
                        </button>
                    )}
                    {onDeleteSessionType && (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            title="Delete Session Type"
                            onClick={() => onDeleteSessionType(row.id)}
                        >
                            <i className="ri-delete-bin-line"></i>
                        </button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "140px",
        },
    ];

    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text("Session Types List", 14, 10);
        const tableColumn = [
            "S.No",
            "Category",
            "Name",
            "Description",
            "Status",
            "Created By",
            "Updated By",
        ];
        const tableRows = [];

        filteredSessions.forEach((s, index) => {
            tableRows.push([
                index + 1,
                s.category?.name || "N/A",
                s.name || "",
                s.description || "",
                s.status || "active",
                s.creator?.name || "",
                s.updater?.name || "",
            ]);
        });

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 20,
        });

        doc.save("session_types_list.pdf");
    };

    return (
        <div>
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                {/* Left: Export + Search */}
                <div className="d-flex align-items-center gap-2 position-relative">
                    <div className="dropdown">
                        <button
                            className="btn btn-label-primary btn-sm dropdown-toggle"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                        >
                            <i className="ri-download-2-line me-1"></i> Export
                        </button>
                        <ul className="dropdown-menu shadow-sm">
                            <li>
                                <button className="dropdown-item" onClick={handleExportPDF}>
                                    <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                                </button>
                            </li>
                            <li>
                                <CSVLink
                                    data={filteredSessions.map((s, index) => ({
                                        "S.No": index + 1,
                                        Category: s.category?.name || "N/A",
                                        Name: s.name || "",
                                        Description: s.description || "",
                                        Status: s.status || "active",
                                        "Created By": s.creator?.name || "",
                                        "Updated By": s.updater?.name || "",
                                    }))}
                                    filename="session_types_export.csv"
                                    className="dropdown-item"
                                >
                                    <i className="ri-file-excel-line me-1 text-success"></i> CSV
                                </CSVLink>
                            </li>
                        </ul>
                    </div>

                    <input
                        type="text"
                        placeholder="Search..."
                        className="form-control form-control-sm"
                        style={{ width: "200px" }}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </div>

                {/* Center Info */}
                <h6 className="mb-0 text-center text-muted flex-grow-1">
                    Showing {start}-{end} of {totalRows} session types
                </h6>

                {/* Right: Add Button */}
                <div>
                    {onAddSessionType && (
                        <button
                            className="btn btn-sm btn-primary"
                            title="Add Session Type"
                            onClick={onAddSessionType}
                        >
                            <i className="ri-add-line me-1"></i>
                            Add Session Type
                        </button>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="card-body p-2">
                <DataTable
                    columns={columns}
                    data={filteredSessions}
                    pagination
                    paginationPerPage={rowsPerPage}
                    paginationTotalRows={totalRows}
                    onChangeRowsPerPage={setRowsPerPage}
                    onChangePage={setCurrentPage}
                    paginationComponent={CustomPagination}
                    highlightOnHover
                    striped
                    responsive
                    progressPending={isLoading}
                    noDataComponent={
                        <div className="p-3 text-muted">
                            {error ? error : "No session types found"}
                        </div>
                    }
                />
            </div>
        </div>
    );
};

export default SessionTypeList;
